interface StepProgressProps {
  currentStep: number;
  steps: { label: string; icon: string }[];
}

function StepNode({
  idx,
  label,
  currentStep,
}: {
  idx: number;
  label: string;
  currentStep: number;
}) {
  const isCompleted = idx < currentStep;
  const isActive = idx === currentStep;

  return (
    <div className="flex flex-col items-center shrink-0 relative z-10">
      <div
        className={`w-11 h-11 rounded-full flex items-center justify-center text-base transition-all duration-300
          ${isCompleted
            ? 'bg-[#1B3D2A] text-white shadow-md shadow-[#1B3D2A]/20'
            : isActive
              ? 'bg-[#1B3D2A] text-white ring-[3px] ring-[#1B3D2A]/15 shadow-md shadow-[#1B3D2A]/20'
              : 'bg-[#FAF8F5] text-[#9A8A7A] border-2 border-[#E8E0D6]'
          }`}
      >
        {isCompleted ? (
          <i className="ri-check-line" />
        ) : (
          <span className="text-sm font-bold">{idx + 1}</span>
        )}
        {isActive && (
          <span className="absolute inset-0 rounded-full bg-[#1B3D2A] animate-ping opacity-10" />
        )}
      </div>
      <span
        className={`mt-2 text-xs font-semibold whitespace-nowrap transition-colors duration-300
          ${isCompleted || isActive ? 'text-[#1B3D2A]' : 'text-[#9A8A7A]'}`}
      >
        {label}
      </span>
      {isActive && (
        <span className="absolute -bottom-1 w-1 h-1 rounded-full bg-[#D4A574]" />
      )}
    </div>
  );
}

function Connector({ isCompleted }: { isCompleted: boolean }) {
  return (
    <div className="flex-1 flex items-center h-0 mx-2 md:mx-4">
      <div className="w-full h-[2px] bg-[#E8E0D6] rounded-full relative overflow-hidden">
        <div
          className="absolute inset-y-0 left-0 bg-[#1B3D2A] rounded-full transition-all duration-700 ease-out"
          style={{ width: isCompleted ? '100%' : '0%' }}
        />
      </div>
    </div>
  );
}

export default function StepProgress({ currentStep, steps }: StepProgressProps) {
  return (
    <div className="bg-white rounded-2xl border border-[#E8E0D6] p-5 md:p-6">
      {/* Header */}
      <div className="flex items-center gap-2 mb-5 md:mb-6">
        <div className="w-8 h-8 rounded-lg bg-[#1B3D2A]/10 flex items-center justify-center">
          <i className="ri-route-line text-[#1B3D2A]" />
        </div>
        <div>
          <h3 className="text-sm font-bold text-[#1B3D2A]">預約流程</h3>
          <p className="text-xs text-[#9A8A7A]">完成以下 3 個步驟即可預約</p>
        </div>
      </div>

      {/* Progress bar */}
      <div className="mb-6 md:mb-7">
        <div className="w-full h-1.5 bg-[#FAF8F5] rounded-full overflow-hidden">
          <div
            className="h-full bg-[#1B3D2A] rounded-full transition-all duration-700 ease-out"
            style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
          />
        </div>
        <div className="flex justify-between mt-1.5">
          <span className="text-xs text-[#9A8A7A]">進度</span>
          <span className="text-xs font-semibold text-[#1B3D2A]">
            {currentStep + 1} / {steps.length}
          </span>
        </div>
      </div>

      {/* Steps */}
      <div className="flex items-center">
        {steps.map((step, idx) => (
          <>
            <StepNode key={step.label} idx={idx} label={step.label} currentStep={currentStep} />
            {idx < steps.length - 1 && (
              <Connector key={`conn-${idx}`} isCompleted={idx < currentStep} />
            )}
          </>
        ))}
      </div>
    </div>
  );
}